/**
 * useLobbyBots — the host's bot actions, shared by the lobby (seat "Add bot",
 * "Fill with bots") and the Add Players screen.
 *
 *   addBot(profile?, slot?)  POST /:id/add-bot { botProfile?, slot? } — the
 *                     server seats the bot in `slot` (a Pick-seats team lobby's
 *                     chosen seat) or else the lowest open seat.
 *   fillWithBots()    one add-bot call per open (unreserved) seat, sequentially
 *                     so each request sees the seat the previous one consumed
 *                     and auto-start only fires after the last seat fills.
 *
 * Feedback is returned (addedNote / actionError / picker statuses) for the
 * caller to render inline.
 */

import React from 'react'
import type { GameResponse } from '@warcarp/api-types'
import { useAddBot } from './useGames'
import { computeSeatMap } from '../lib/seatMap'
import { randomBotProfilesForSeats } from '../lib/botPicker'

export type BotPickerStatus = 'added' | 'lobby'

export function useLobbyBots(game: GameResponse | undefined | null) {
  const addBotMutation = useAddBot(game?.id ?? '')
  const [addedNote, setAddedNote] = React.useState<string | null>(null)
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [isFilling, setIsFilling] = React.useState(false)
  const [statuses, setStatuses] = React.useState<Record<string, BotPickerStatus>>({})
  const statusTimers = React.useRef(new Map<string, ReturnType<typeof setTimeout>>())

  React.useEffect(() => {
    if (!addedNote) return
    const t = setTimeout(() => setAddedNote(null), 3000)
    return () => clearTimeout(t)
  }, [addedNote])
  React.useEffect(() => {
    const timers = statusTimers.current
    return () => {
      for (const timer of timers.values()) clearTimeout(timer)
    }
  }, [])

  const openSeats = game
    ? computeSeatMap(game.players, game.invitations, game.maxPlayers).filter((s) => s.kind === 'open').length
    : 0

  async function addBot(botProfile?: string, slot?: number) {
    if (!game) return
    setActionError(null)
    // Diff the roster before/after so we can name the bot that was added.
    const before = new Set(game.players.map((p) => p.id))
    try {
      const body = { ...(botProfile ? { botProfile } : {}), ...(slot != null ? { slot } : {}) }
      const updated = await addBotMutation.mutateAsync(Object.keys(body).length ? body : undefined)
      const newBot = updated.players.find((p) => !before.has(p.id))
      if (botProfile) {
        setStatuses((current) => ({ ...current, [botProfile]: 'added' }))
        const existingTimer = statusTimers.current.get(botProfile)
        if (existingTimer) clearTimeout(existingTimer)
        const timer = setTimeout(() => {
          setStatuses((current) => ({ ...current, [botProfile]: 'lobby' }))
          statusTimers.current.delete(botProfile)
        }, 1400)
        statusTimers.current.set(botProfile, timer)
      }
      setAddedNote(newBot ? `Added ${newBot.displayName}` : 'Bot added')
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Could not add bot')
    }
  }

  async function fillWithBots() {
    if (!game) return
    const profiles = randomBotProfilesForSeats(game.players, openSeats)
    if (profiles.length < openSeats) {
      setActionError('Not enough bot profiles are available to fill every open seat')
      return
    }
    setActionError(null)
    setAddedNote(null)
    setIsFilling(true)
    let addedCount = 0
    try {
      for (const botProfile of profiles) {
        await addBotMutation.mutateAsync({ botProfile })
        addedCount++
      }
      setAddedNote(`Added ${addedCount} random bot${addedCount === 1 ? '' : 's'}`)
    } catch (err: unknown) {
      const reason = err instanceof Error ? err.message : 'Could not add a bot'
      setActionError(addedCount > 0 ? `Added ${addedCount} before filling stopped: ${reason}` : reason)
    } finally {
      setIsFilling(false)
    }
  }

  return {
    addBot,
    fillWithBots,
    openSeats,
    isPending: addBotMutation.isPending,
    isFilling,
    statuses,
    addedNote,
    actionError,
    clearError: () => setActionError(null),
  }
}
